import {
  BellRing,
  Cable,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  DoorOpen,
  KeyRound,
  ShieldCheck,
  UserCheck,
  Wrench,
} from "lucide-react";
import { CategoryImage, CategoryPoint, ContentBlock } from "@/types";

export type SecurityServiceContentInput = {
  serviceName: string;
  summary: string;
  suitedTo: string[];
  includes: string[];
  notes: string[];
  process: string[];
  faq: string[];
};

const joinList = (items: string[]) => items.join(", ");

export const toOptimizedImagePath = (value: string) =>
  value.replace(/\.(jpe?g|png)$/i, ".webp");

export const createSecurityServiceContent = ({
  serviceName,
  summary,
  suitedTo,
  includes,
  notes,
  process,
  faq,
}: SecurityServiceContentInput): ContentBlock[] => [
  {
    id: 1,
    title: `What ${serviceName} includes`,
    content: summary,
    points: [
      `Best suited to: ${joinList(suitedTo)}.`,
      `Typical work: ${joinList(includes)}.`,
      `Site considerations: ${joinList(notes)}.`,
    ],
  },
  {
    id: 2,
    title: "How Ozy Wide Group approaches the site",
    content:
      "Each enquiry is planned around the property layout, access points, daily routines, existing systems and the people who need to use or manage the security setup. The aim is practical protection that works after installation or coverage begins, not a generic list of equipment.",
    points: process,
  },
  {
    id: 3,
    title: "What to prepare before booking",
    content:
      "A short brief is usually enough to scope the next step. Helpful details include the suburb, property type, access points, recent incidents, photos, existing equipment, operating hours and whether guarding, patrols or system installation is urgent.",
    points: [
      "Send photos of entries, gates, camera locations, existing alarm panels or areas of concern.",
      "Share site plans, tenancy details, staff access requirements or incident history if available.",
      "Confirm whether the priority is deterrence, monitoring, access control, incident response or compliance support.",
    ],
  },
  {
    id: 4,
    title: "Common questions",
    content:
      "Ozy Wide Group can advise on equipment, staffing, patrol routines and security sequencing once the site conditions and priority risks are clear.",
    points: faq,
  },
];

export const createServicePoints = (
  planning = "Risk and access points reviewed",
  install = "Practical security setup",
  handover = "Clear handover and support",
): CategoryPoint[] => [
  { title: planning, icon: ClipboardCheck },
  { title: install, icon: Wrench },
  { title: handover, icon: ShieldCheck },
];

export const electronicCategoryPoints: CategoryPoint[] = [
  { title: "CCTV, alarms and intercoms", icon: Camera },
  { title: "Access and gate control", icon: KeyRound },
  { title: "Maintenance-ready setup", icon: CheckCircle2 },
];

export const guardingCategoryPoints: CategoryPoint[] = [
  { title: "Licensed guard support", icon: UserCheck },
  { title: "Patrol and response planning", icon: ShieldCheck },
  { title: "Fire alarm and cabling support", icon: BellRing },
];

export const createImageItems = (
  images: { id: number; original: string }[],
  altBase: string,
): CategoryImage[] =>
  images.map((image, index) => {
    const alt = `${altBase} - ${index + 1}`;
    const original = toOptimizedImagePath(image.original);

    return {
      ...image,
      original,
      url: original,
      thumbnail: original,
      alt,
      title: altBase,
      originalAlt: alt,
      originalTitle: altBase,
      thumbnailAlt: alt,
      thumbnailTitle: altBase,
    };
  });

export const categoryIconMap = {
  electronic: DoorOpen,
  guarding: UserCheck,
  cabling: Cable,
};
